import { RootState } from '@AppState/redux-store'
import { ApiKeysServiceAPI } from '@API/data-services/api-keys-service'
import { BackOfficeApiKeyRecord } from '@Generated/back-office-client/src'
import {
  Badge,
  Box,
  Button,
  Callout,
  CheckboxGroup,
  Flex,
  Heading,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes'
import { Form } from 'radix-ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Routes } from '@Components/main-app/types'
import { ScopeUsageSection } from './scope-usage-section'
import { NO_SCOPES_MESSAGE, useAvailableScopes } from './use-available-scopes'

export const EditApiKey = () => {
  const nav = useNavigate()
  const params = useParams()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)

  const keyId = params.keyId
  const keyForEdit = keyId ? (location.state as BackOfficeApiKeyRecord | null) : null

  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [scopesError, setScopesError] = useState(false)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (keyId && !keyForEdit) {
      nav(Routes.API_KEYS)
    } else if (keyForEdit) {
      setSelectedScopes(keyForEdit.scopes)
      setIsActive(keyForEdit.isActive)
    }
  }, [keyId, keyForEdit, nav])

  const { availableScopes, scopesLoading, scopesErrorMessage } = useAvailableScopes(
    keyForEdit?.service,
  )

  const [updateError, setUpdateError] = useState<string | null>(null)

  const { mutate: updateKey, isPending } = useMutation({
    mutationFn: (updates: { description?: string; scopes?: string[]; isActive?: boolean }) => {
      return ApiKeysServiceAPI.updateKey(keyId!, {
        ...updates,
        service: keyForEdit!.service,
      })
    },
    onSuccess: () => {
      setUpdateError(null)
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-api-keys`] })
      nav(Routes.API_KEYS)
    },
    onError: () => {
      setUpdateError('Failed to update API key')
    },
  })

  if (!keyForEdit) {
    return null
  }

  return (
    <Form.Root
      onSubmit={(e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget).entries()
        const data = Object.fromEntries(formData)

        if (!selectedScopes.length) {
          setScopesError(true)
          return
        }

        updateKey({
          description: data.description as string,
          scopes: selectedScopes,
          isActive,
        })
      }}>
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="bold">
            Service
          </Text>
          <Text size="2" color="gray">
            {keyForEdit.service}
          </Text>
        </Flex>

        <Form.Field name="description">
          <Flex justify="between">
            <Form.Label>Description</Form.Label>
            <Form.Message className="FormMessage" match="valueMissing">
              Description is required
            </Form.Message>
          </Flex>

          <Form.Control asChild required>
            <TextField.Root defaultValue={keyForEdit.description} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="scopes">
          <Flex justify="between">
            <Form.Label>Scopes</Form.Label>
            {scopesError ? (
              <Form.Message className="FormMessage">At least one scope is required</Form.Message>
            ) : null}
          </Flex>

          {keyForEdit.fullAccess ? (
            <Badge color="blue" variant="solid" size="2">
              Full Access
            </Badge>
          ) : scopesLoading ? (
            <Text size="2" color="gray">
              Loading scopes...
            </Text>
          ) : availableScopes?.length ? (
            <CheckboxGroup.Root
              value={selectedScopes}
              onValueChange={(checkedValues) => {
                setSelectedScopes(checkedValues)
                if (checkedValues.length) {
                  setScopesError(false)
                }
              }}>
              {availableScopes.map((scope) => (
                <CheckboxGroup.Item key={scope} value={scope}>
                  {scope}
                </CheckboxGroup.Item>
              ))}
            </CheckboxGroup.Root>
          ) : (
            <Text size="2" color={scopesErrorMessage ? 'red' : 'gray'}>
              {scopesErrorMessage ?? NO_SCOPES_MESSAGE}
            </Text>
          )}
        </Form.Field>

        <Box mt="4">
          <Heading size="3" mb="2">
            Scope Usage
          </Heading>
          <ScopeUsageSection apiKey={keyForEdit} />
        </Box>

        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="bold">
            Active
          </Text>
          <Switch checked={isActive} onCheckedChange={setIsActive} />
        </Flex>

        {updateError ? (
          <Callout.Root color="red">
            <Callout.Text>{updateError}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3">
          <Form.Submit asChild>
            <Button loading={isPending} style={{ cursor: 'pointer' }}>
              Save Changes
            </Button>
          </Form.Submit>
          <Button
            type="button"
            variant="soft"
            color="gray"
            style={{ cursor: 'pointer' }}
            onClick={() => nav(Routes.API_KEYS)}>
            Cancel
          </Button>
        </Flex>
      </Flex>
    </Form.Root>
  )
}
